import React, { useState, useEffect, useRef } from 'react';
import HeaderWrapper from '../components/common/HeaderWrapper';
import './TermsAndConditionsPage.css';

const TermsAndConditionsPage = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showTOC, setShowTOC] = useState(false);
  const contentScrollRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollContainer = contentScrollRef.current;
      if (!scrollContainer) return;

      const sections = scrollContainer.querySelectorAll('.legal-section');
      let current = '';
      
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        if (scrollContainer.scrollTop >= sectionTop - 150) {
          current = section.getAttribute('id');
        }
      });
      
      setActiveSection(current);
    };

    const scrollContainer = contentScrollRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const scrollToSection = (sectionId) => {
    const scrollContainer = contentScrollRef.current;
    const element = document.getElementById(sectionId);
    if (element && scrollContainer) {
      // Calculate position relative to the scroll container
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop = scrollContainer.scrollTop;
      const elementTop = elementRect.top - containerRect.top + scrollTop;
      
      scrollContainer.scrollTo({ top: elementTop - 20, behavior: 'smooth' });
      setShowTOC(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="home-container body-font">
      <HeaderWrapper />
      <div className="main-background terms-page-background">
        <div className="content-wrapper terms-page-wrapper">
          <main style={{ flex: '0 1 auto' }}>
            {/* Hero Section */}
            <div className="terms-hero-section">
              <h1 className="heading-font terms-hero-title">Terms and Conditions</h1>
              <div className="terms-hero-details">
                <p className="terms-company-name">Yourplat4orm Limited</p>
                <p>Company Number: 15955054</p>
                <p>Registered in England and Wales</p>
                <p className="terms-last-updated">Last Updated: March 2026</p>
              </div>
            </div>

            {/* Mobile TOC Toggle */}
            <button className="toc-mobile-toggle" onClick={() => setShowTOC(!showTOC)}>
              {showTOC ? '✕ Close Menu' : '☰ Table of Contents'}
            </button>

            {/* Scrollable Container Wrapper */}
            <div className="terms-scroll-container">
              <div className="terms-three-column-layout">
                {/* Table of Contents */}
                <nav className={`terms-toc-scroll ${showTOC ? 'terms-toc-visible' : ''}`}>
                  <h3 className="toc-title">Contents</h3>
                  <ul className="toc-list">
                  <li className={activeSection === 'section-intro' ? 'active' : ''} onClick={() => scrollToSection('section-intro')}>Introduction</li>
                  <li className={activeSection === 'section-1' ? 'active' : ''} onClick={() => scrollToSection('section-1')}>1. About Us</li>
                  <li className={activeSection === 'section-2' ? 'active' : ''} onClick={() => scrollToSection('section-2')}>2. Acceptance of Terms</li>
                  <li className={activeSection === 'section-3' ? 'active' : ''} onClick={() => scrollToSection('section-3')}>3. Definitions</li>
                  <li className={activeSection === 'section-4' ? 'active' : ''} onClick={() => scrollToSection('section-4')}>4. Eligibility</li>
                  <li className={activeSection === 'section-5' ? 'active' : ''} onClick={() => scrollToSection('section-5')}>5. Creative Pages</li>
                  <li className={activeSection === 'section-6' ? 'active' : ''} onClick={() => scrollToSection('section-6')}>6. Content Requirements</li>
                  <li className={activeSection === 'section-7' ? 'active' : ''} onClick={() => scrollToSection('section-7')}>7. User Content Access</li>
                  <li className={activeSection === 'section-8' ? 'active' : ''} onClick={() => scrollToSection('section-8')}>8. Payments & Credits</li>
                  <li className={activeSection === 'section-9' ? 'active' : ''} onClick={() => scrollToSection('section-9')}>9. Intellectual Property</li>
                  <li className={activeSection === 'section-10' ? 'active' : ''} onClick={() => scrollToSection('section-10')}>10. Moderation</li>
                  <li className={activeSection === 'section-11' ? 'active' : ''} onClick={() => scrollToSection('section-11')}>11. Prohibited Conduct</li>
                  <li className={activeSection === 'section-12' ? 'active' : ''} onClick={() => scrollToSection('section-12')}>12. Anti-Piracy</li>
                  <li className={activeSection === 'section-13' ? 'active' : ''} onClick={() => scrollToSection('section-13')}>13. Data Protection</li>
                  <li className={activeSection === 'section-14' ? 'active' : ''} onClick={() => scrollToSection('section-14')}>14. Limitation of Liability</li>
                  <li className={activeSection === 'section-15' ? 'active' : ''} onClick={() => scrollToSection('section-15')}>15. Account Termination</li>
                  <li className={activeSection === 'section-16' ? 'active' : ''} onClick={() => scrollToSection('section-16')}>16. Changes to Terms</li>
                  <li className={activeSection === 'section-17' ? 'active' : ''} onClick={() => scrollToSection('section-17')}>17. Governing Law</li>
                  <li className={activeSection === 'section-18' ? 'active' : ''} onClick={() => scrollToSection('section-18')}>18. AI Statement</li>
                  <li className={activeSection === 'section-19' ? 'active' : ''} onClick={() => scrollToSection('section-19')}>19. Online Safety Act</li>
                  <li className={activeSection === 'section-20' ? 'active' : ''} onClick={() => scrollToSection('section-20')}>20. Credit Purchase</li>
                  <li className={activeSection === 'section-21' ? 'active' : ''} onClick={() => scrollToSection('section-21')}>21. Privacy Policy</li>
                  <li className={activeSection === 'section-22' ? 'active' : ''} onClick={() => scrollToSection('section-22')}>22. Dispute Resolution</li>
                  </ul>
                </nav>

                {/* Legal Content - Scrollable */}
                <div className="terms-content-scroll" ref={contentScrollRef}>
                
                {/* Introduction Section */}
                <section id="section-intro" className="legal-section introduction-section">
                  <h2 className="heading-font intro-main-title">Yourplat4orm: Terms & Conditions</h2>
                  
                  <h3 className="heading-font intro-subtitle">Introduction</h3>
                  
                  <div className="intro-vision">
                    <h4 className="heading-font">The Vision Thing</h4>
                    <p>We have seen what the free internet looks like and it's time for a new approach.</p>
                    <p>Getting paid as a creative is hard, and it's getting harder. We are trying to build a platform where creative people can get paid for their creativity.</p>
                    <p>We particularly aim to support professional, experienced, working creatives like musicians, writers and photographers who can no longer make a living.</p>
                  </div>

                  <h4 className="heading-font intro-deal-title">Here's the deal…</h4>

                  <div className="intro-section-box">
                    <h5 className="heading-font">Creatives</h5>
                    <p>Stop giving your work away for free.</p>
                    <p>Get a page. Load content. Set a price. Get half of whatever income that content generates.</p>
                    <p>Yourplat4orm is not open to the public, you can only get a page if we invite you or if your application is approved.</p>
                    <p>Everything you post must be original and your own work – no covers or re-posts.</p>
                    <p>You can make some of your content free to access.</p>
                    <p>The prices you set should be realistic, if you get no sales you could lose your page. We can help plan your pricing strategy.</p>
                    <p>AI content is acceptable but MUST be acknowledged when posting content – choose 0 (no AI) – 5 (all AI)</p>
                    <p>Adult content must be clearly marked 18+</p>
                    <p>If anyone copies and reposts your content without permission tell us and we will help you to stop them. There's too much of this happening and we need to stand together to defend our intellectual property.</p>
                    <p>Placing your content behind a paywall makes it more difficult for AI / LLM's to access.</p>
                    <p>You will get any money you're owed in the next complete month after the payment is received. Minimum payment is £50. If you have earned less than that it will be rolled over into the next month's payment.</p>
                    <p>Any content that does not comply with our policies will be removed and any income generated from it may be forfeit.</p>
                  </div>

                  <div className="intro-section-box">
                    <h5 className="heading-font">Users</h5>
                    <p>Browse free content anytime.</p>
                    <p>Register and buy 50 or 100 credits.</p>
                    <p>Spend them anywhere on Yourplat4orm.</p>
                    <p>Support your favourite creatives and find new ones to follow. You can be confident that the creative uploader will get half of whatever you spend accessing their content.</p>
                    <p>Don't steal it and re-post it. It's behind a paywall for a reason – let's try and get these people making a living again.</p>
                    <p>The ownership of the content remains with the creative, if you re-post it without permission you will be stealing from them.</p>
                    <p>All content is moderated by an actual human. If we're not sure it complies with our policies it will be removed pending moderation.</p>
                  </div>

                  <div className="intro-section-box">
                    <h5 className="heading-font">Yourplat4orm</h5>
                    <p>We will never claim ownership of your content, it remains the property of the creator who gives us permission to transact on their behalf.</p>
                    <p>We are determined to keep an orderly site, we intend to keep out the bots, spammers, scammers and extremists. We reserve the right to refuse any and all services without explanation.</p>
                    <p>We are real human Mancunians, we won't hide behind an AI chatbot or a legal team – send us an email and we'll read it and respond, but we're not idiots either, if you mess us around you'll lose your page.</p>
                    <p>We will never sell your data.</p>
                  </div>

                  <div className="intro-divider">
                    <h3 className="heading-font">YOURPLAT4ORM – FULL TERMS & CONDITIONS</h3>
                    <p className="terms-last-updated">Last updated: 21st March 2026</p>
                  </div>
                </section>

                {/* Section 1: About Us */}
                <section id="section-1" className="legal-section">
                  <h2 className="heading-font">1. About Us</h2>
                  <p><strong>1.1</strong> The platform known as Yourplat4orm ("the Platform") is operated by Yourplat4orm Limited, a company registered in England and Wales under company number 15955054, with its registered office at Suite 5, 4th Floor, 3 Universal Square, Devonshire Street North, Manchester, United Kingdom, M12 6JH.</p>
                  <p><strong>1.2</strong> We can be contacted at: <a href="mailto:admin@yourplat4orm.com">admin@yourplat4orm.com</a></p>
                  <p><strong>1.3</strong> These Terms and Conditions ("Terms") set out the rules for Creatives and Users when accessing or interacting with the Platform.</p>
                </section>

                {/* Section 2 */}
                <section id="section-2" className="legal-section">
                  <h2 className="heading-font">2. Acceptance of These Terms</h2>
                  <p><strong>2.1</strong> By creating an account, applying for a Creative Page, purchasing credits, or accessing any content on the Platform, you agree to be bound by these Terms.</p>
                  <p><strong>2.2</strong> If you do not agree to these Terms, you must not use the Platform.</p>
                </section>

                {/* Section 3 */}
                <section id="section-3" className="legal-section">
                  <h2 className="heading-font">3. Definitions</h2>
                  <ul>
                    <li><strong>Creative:</strong> An approved or invited content creator who uploads original works to the Platform.</li>
                    <li><strong>User:</strong> Any individual who accesses or purchases content on the Platform.</li>
                    <li><strong>Creative Page:</strong> The page assigned to an approved Creative where their content is displayed.</li>
                    <li><strong>Credits:</strong> Digital credits purchased by Users to access paid content.</li>
                    <li><strong>Content:</strong> Any material uploaded by a Creative, including text, images, video, audio, music, AI-generated material, or any other creative work.</li>
                  </ul>
                </section>

                {/* Section 4 */}
                <section id="section-4" className="legal-section">
                  <h2 className="heading-font">4. Eligibility</h2>
                  <p><strong>4.1</strong> Creator access is invite-only or based on successful application approval. We may refuse any application without explanation.</p>
                </section>

                {/* Section 5 */}
                <section id="section-5" className="legal-section">
                  <h2 className="heading-font">5. Creative Pages and Platform Access</h2>
                  <p><strong>5.1</strong> Creatives may only obtain a Creative Page by direct invitation or approval of an application.</p>
                  <p><strong>5.2</strong> We may remove a Creative Page at our discretion, including where:</p>
                  <ul>
                    <li>pricing is unrealistic and sales remain consistently low;</li>
                    <li>content breaches our policies;</li>
                    <li>behaviour is abusive, dishonest, or manipulative; or</li>
                    <li>we reasonably believe continued access would harm the Platform.</li>
                  </ul>
                  <p><strong>5.3</strong> Changes, refusals, or removals may occur without prior notice.</p>
                </section>

                {/* Section 6 */}
                <section id="section-6" className="legal-section">
                  <h2 className="heading-font">6. Content Requirements for Creatives</h2>
                  <p><strong>6.1</strong> All content must be completely original and created by the Creative.</p>
                  <ul>
                    <li>No covers</li>
                    <li>No reposts</li>
                    <li>No material owned by third parties</li>
                  </ul>
                  <p><strong>6.2</strong> Creatives must accurately tag any use of AI in their content using the 0–5 scale provided by the Platform.</p>
                  <p><strong>6.3</strong> Adult content is permitted only if clearly marked as 18+.</p>
                  <p><strong>6.4</strong> Content may be set as free or paid, at the Creative's discretion.</p>
                  <p><strong>6.5</strong> Any content that does not comply with these Terms or our policies may be removed. Any income generated from non-compliant content may be forfeited.</p>
                </section>

                {/* Section 7 */}
                <section id="section-7" className="legal-section">
                  <h2 className="heading-font">7. User Content Access and Restrictions</h2>
                  <p><strong>7.1</strong> Users may browse free content at any time.</p>
                  <p><strong>7.2</strong> To access paid content, Users must purchase either 50 or 100 credits.</p>
                  <p><strong>7.3</strong> Paid content is strictly for personal viewing only. Users must not:</p>
                  <ul>
                    <li>copy, save, download, record, screenshot, distribute, repost, or otherwise reproduce any content;</li>
                    <li>make content publicly accessible or share it outside the Platform;</li>
                    <li>attempt to circumvent the paywall.</li>
                  </ul>
                  <p><strong>7.4</strong> Reposting or redistributing content without permission is an infringement of copyright and may result in account suspension, legal action, and reporting to rights-protection authorities.</p>
                </section>

                {/* Section 8 */}
                <section id="section-8" className="legal-section">
                  <h2 className="heading-font">8. Payments, Credits and Revenue Sharing</h2>
                  <h3>For Users</h3>
                  <p><strong>8.1</strong> Credits purchased on the Platform are non-refundable unless required by law.</p>
                  <p><strong>8.2</strong> Credits have no cash value outside the Platform and cannot be exchanged for money.</p>
                  <h3>For Creatives</h3>
                  <p><strong>8.3</strong> Creatives receive 50% of the income generated from paid access to their content.</p>
                  <p><strong>8.4</strong> Payouts are made monthly, in the next complete month after the revenue is received.</p>
                  <p><strong>8.5</strong> Minimum payout is £50. Amounts under £50 will roll over to the next month.</p>
                  <p><strong>8.6</strong> Creatives are responsible for ensuring that their provided payout details are accurate. We are not liable for delays caused by incorrect information.</p>
                </section>

                {/* Section 9 */}
                <section id="section-9" className="legal-section">
                  <h2 className="heading-font">9. Intellectual Property</h2>
                  <h3>Creative Content</h3>
                  <p><strong>9.1</strong> Creatives retain full ownership and copyright of all content they upload.</p>
                  <p><strong>9.2</strong> By uploading content, Creatives grant us a limited licence to:</p>
                  <ul>
                    <li>host, display, and distribute their content on the Platform;</li>
                    <li>transact on their behalf and collect revenue;</li>
                    <li>use content for Platform promotion only with permission.</li>
                  </ul>
                  <p><strong>9.3</strong> We will never claim ownership of Creative content.</p>
                  <h3>Platform Rights</h3>
                  <p><strong>9.4</strong> All platform software, branding, design, and materials belong to us or our licensors.</p>
                  <p><strong>9.5</strong> Users and Creatives must not replicate or reverse-engineer the Platform.</p>
                </section>

                {/* Section 10 */}
                <section id="section-10" className="legal-section">
                  <h2 className="heading-font">10. Moderation and Platform Integrity</h2>
                  <p><strong>10.1</strong> All content is moderated by real human moderators.</p>
                  <p><strong>10.2</strong> If content appears questionable or is suspected of breaching our policies, we may remove it pending review.</p>
                  <p><strong>10.3</strong> We are committed to keeping out bots, spammers, scammers, extremist material, and abusive behaviour.</p>
                  <p><strong>10.4</strong> We may refuse any service, or remove any account, without explanation where necessary to maintain a safe and orderly environment.</p>
                </section>

                {/* Section 11 */}
                <section id="section-11" className="legal-section">
                  <h2 className="heading-font">11. Prohibited Conduct</h2>
                  <p>You must not:</p>
                  <ul>
                    <li>upload illegal, harmful, defamatory, hateful, or extremist material;</li>
                    <li>impersonate any person or misrepresent your identity;</li>
                    <li>attempt to bypass moderation or security measures;</li>
                    <li>use automated scripts or bots;</li>
                    <li>harass, abuse, or manipulate other users or Creatives;</li>
                    <li>interfere with the operation of the Platform.</li>
                  </ul>
                </section>

                {/* Section 12 */}
                <section id="section-12" className="legal-section">
                  <h2 className="heading-font">12. Anti-Piracy Support</h2>
                  <p><strong>12.1</strong> If a Creative believes their work has been copied or reposted without permission, they should notify us immediately.</p>
                  <p><strong>12.2</strong> We will provide reasonable assistance to help them take action to protect their intellectual property.</p>
                </section>

                {/* Section 13 */}
                <section id="section-13" className="legal-section">
                  <h2 className="heading-font">13. Data Protection and Privacy</h2>
                  <p><strong>13.1</strong> We comply with UK GDPR and the Data Protection Act 2018.</p>
                  <p><strong>13.2</strong> We will never sell your data.</p>
                  <p><strong>13.3</strong> For full details of how we handle personal information, please see our Privacy Policy.</p>
                </section>

                {/* Section 14 */}
                <section id="section-14" className="legal-section">
                  <h2 className="heading-font">14. Limitation of Liability</h2>
                  <p><strong>14.1</strong> Nothing in these Terms excludes liability for death, personal injury caused by negligence, fraud, or any other liability which cannot be excluded by law.</p>
                  <p><strong>14.2</strong> The Platform is provided "as is" and "as available". We do not guarantee uninterrupted or error-free access.</p>
                  <p><strong>14.3</strong> To the maximum extent permitted by law, we are not liable for:</p>
                  <ul>
                    <li>loss of profits, business, or revenue;</li>
                    <li>loss or corruption of data;</li>
                    <li>indirect or consequential losses.</li>
                  </ul>
                  <p><strong>14.4</strong> The views and opinions expressed in articles published on this website are those of the respective authors and do not necessarily reflect the views of the website or its owners. All content provided by third-party contributors is published in good faith; however, the website and its owners do not accept responsibility or liability for any statements that may be considered defamatory, libellous, or otherwise unlawful. Responsibility for the accuracy and legality of all content rests solely with the individual authors. The website and its owners disclaim all liability for any loss or damage arising from reliance on such content.</p>
                </section>

                {/* Section 15 */}
                <section id="section-15" className="legal-section">
                  <h2 className="heading-font">15. Account Suspension or Termination</h2>
                  <p><strong>15.1</strong> We may suspend or terminate access for breach of these Terms or where we believe it is necessary to protect the Platform, our Creatives, or Users.</p>
                  <p><strong>15.2</strong> Creatives who misuse the Platform, breach content rules, or fail to act in good faith may lose their Creative Page.</p>
                  <p><strong>15.3</strong> You may close your account at any time by contacting us.</p>
                </section>

                {/* Section 16 */}
                <section id="section-16" className="legal-section">
                  <h2 className="heading-font">16. Changes to These Terms</h2>
                  <p><strong>16.1</strong> We may update these Terms from time to time.</p>
                  <p><strong>16.2</strong> Significant changes will be posted on the Platform and/or emailed to Creatives and Users.</p>
                  <p><strong>16.3</strong> Continued use of the Platform after changes take effect constitutes acceptance of the updated Terms.</p>
                </section>

                {/* Section 17 */}
                <section id="section-17" className="legal-section">
                  <h2 className="heading-font">17. Governing Law</h2>
                  <p><strong>17.1</strong> These Terms are governed by English law.</p>
                  <p><strong>17.2</strong> Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
                </section>

                {/* Section 18 */}
                <section id="section-18" className="legal-section">
                  <h2 className="heading-font">18. AI Statement</h2>
                  <p>Yourplat4orm exists to enable and encourage creative endeavour. Use of AI to create posts is acceptable, however, we request creative uploaders who have used AI, select an appropriate number (0-5) on the AI scale in the upload area.</p>
                  <p>Using AI to alter other people's work and then claiming it as your own is not acceptable and this content will be removed.</p>
                  <p>We understand that placing your content behind a paywall makes it more difficult for AI / LLM's to access, we suggest posting your content behind even the smallest paywalled amount (1 credit = £0.10) in order to prevent your content appearing in an 'AI Overview'.</p>
                </section>

                {/* Section 19 */}
                <section id="section-19" className="legal-section">
                  <h2 className="heading-font">19. Online Safety Act Compliance</h2>
                  <p>Yourplat4orm.com is committed to complying with the Online Safety Act 2023 and applicable UK legislation. We implement proportionate systems and processes designed to reduce the risk of harm to users, including the presence and dissemination of illegal content and content harmful to children, while respecting users' rights to freedom of expression in line with the Human Rights Act 1998.</p>
                  
                  <h3>19.1. Our Legal Duties</h3>
                  <p>We fulfil our statutory obligations by:</p>
                  <ul>
                    <li>Assessing the risk of illegal content and harm to users, including children, on our platform</li>
                    <li>Implementing appropriate safeguards to mitigate identified risks</li>
                    <li>Regularly reviewing and updating our safety measures in line with guidance from Ofcom</li>
                  </ul>

                  <h3>19.2. Illegal Content</h3>
                  <p>We prohibit and take action against content that constitutes a criminal offence under UK law. This includes, but is not limited to:</p>
                  <ul>
                    <li>Terrorism-related content</li>
                    <li>Child sexual exploitation and abuse material</li>
                    <li>Encouragement or assistance of suicide or self-harm</li>
                    <li>Fraud, scams, and financial exploitation</li>
                    <li>The sale or facilitation of illegal goods, including controlled drugs and weapons</li>
                    <li>Harassment, stalking, and credible threats of violence</li>
                    <li>Offences under the National Security Act 2023 and other relevant laws</li>
                  </ul>
                  <p>Content identified as illegal will be removed as soon as reasonably practicable following assessment. We may suspend or terminate accounts and, where appropriate, report matters to law enforcement authorities.</p>

                  <h3>19.3. Content Harmful to Children</h3>
                  <p>We take additional steps to protect users under 18 from exposure to content that may be harmful or age-inappropriate, including:</p>
                  <ul>
                    <li>Implementing age assurance measures where appropriate</li>
                    <li>Restricting access to certain categories of content</li>
                    <li>Providing safer default settings for younger users</li>
                  </ul>

                  <h3>19.4. Systems and Processes</h3>
                  <p>We maintain and continuously improve systems designed to detect and address harmful content, including:</p>
                  <ul>
                    <li>Content moderation (automated and human review)</li>
                    <li>User reporting tools that are easy to access and use</li>
                    <li>Internal escalation and review procedures for high-risk content</li>
                  </ul>

                  <h3>19.5. User Reporting and Complaints</h3>
                  <p>Users can report content or accounts that may violate this policy. We provide:</p>
                  <ul>
                    <li>Accessible reporting mechanisms</li>
                    <li>Timely review of reports</li>
                    <li>A complaints and appeals process where users can challenge moderation decisions</li>
                  </ul>

                  <h3>19.6. Freedom of Expression and Fairness</h3>
                  <p>We are committed to protecting lawful expression. Our moderation decisions are:</p>
                  <ul>
                    <li>Proportionate and evidence-based</li>
                    <li>Applied consistently</li>
                    <li>Designed to minimise unnecessary removal of lawful content, including journalistic and democratic material</li>
                  </ul>

                  <h3>19.7. Data Protection and Privacy</h3>
                  <p>Our safety measures are implemented in compliance with the Data Protection Act 2018 and UK GDPR. We:</p>
                  <ul>
                    <li>Process personal data lawfully, fairly, and transparently</li>
                    <li>Limit data collection to what is necessary</li>
                    <li>Apply safeguards to protect user privacy, particularly in age assurance processes</li>
                  </ul>

                  <h3>19.8. Enforcement</h3>
                  <p>Where violations occur, we may take appropriate action, including:</p>
                  <ul>
                    <li>Content removal</li>
                    <li>Account suspension or termination</li>
                    <li>Restricting platform features</li>
                    <li>Referral to relevant authorities where required by law</li>
                  </ul>

                  <h3>19.9. Scope</h3>
                  <p>This policy applies to all users of Yourplat4orm.com and governs content accessible within the United Kingdom.</p>
                  <p>We will continue to update this policy to reflect legal developments and regulatory guidance.</p>
                </section>

                {/* Section 20 */}
                <section id="section-20" className="legal-section">
                  <h2 className="heading-font">20. Credit Purchase and Refund Policy</h2>
                  <p>This Policy governs the purchase and use of digital credits ("Credits") on the Platform. By completing a purchase, you acknowledge and agree to these terms, which are drafted in accordance with the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013 and the Consumer Rights Act 2015.</p>
                  
                  <h3>20.1. No Right to Cancellation or Refund</h3>
                  <p>In accordance with Statutory Instrument 2013 No. 3134, by purchasing Credits, you provide express consent for the digital content to be supplied to your account immediately. You acknowledge that once the Credits are credited to your account, you waive your statutory 14-day right to cancel the transaction. Consequently, all purchases of Credits are final and non-refundable. No refunds shall be issued for "change of mind" or unused balances.</p>

                  <h3>20.2. Perpetual Validity of Credits</h3>
                  <p>Credits purchased on the Platform do not have an expiration date. They shall remain available for use within your account for an indefinite period, subject to the continued operation of the Platform and your compliance with our general Terms of Service.</p>

                  <h3>20.3. Non-Transferability and No Cash Value</h3>
                  <p>Credits are non-transferable and are licensed to the specific account holder who performed the purchase. Credits do not constitute a monetary balance, hold no cash value, and cannot be redeemed, exchanged, or "cashed out" for legal tender at any time.</p>

                  <h3>20.4. Statutory Rights and Service Malfunction</h3>
                  <p>Nothing in this Policy affects your statutory rights under the Consumer Rights Act 2015. If the Credits or the digital services they provide access to are not of satisfactory quality, fit for purpose, or as described due to a technical fault within our control, we will provide a repair or replacement of the digital content. A refund will only be considered if a repair or replacement is impossible or fails to resolve the issue within a reasonable timeframe.</p>

                  <h3>20.5. Account Termination</h3>
                  <p>In the event of account termination due to a breach of our Terms of Service, any remaining Credit balance shall be forfeited. The Platform is under no obligation to compensate the user for forfeited Credits resulting from a breach of contract.</p>
                </section>

                {/* Section 21 */}
                <section id="section-21" className="legal-section">
                  <h2 className="heading-font">21. Privacy Policy</h2>
                  <p>This Privacy Policy outlines the practices of Yourplat4orm Limited ("the Company") regarding the collection, use, and disclosure of information processed through this website. This policy is drafted in accordance with the Data Protection Act 2018 and the UK General Data Protection Regulation (UK GDPR).</p>
                  
                  <h3>21.1. Data Collection and Scope</h3>
                  <p>Data collection is limited to that deemed necessary for the functioning of the site and:</p>
                  <ul>
                    <li><strong>Voluntary Correspondence:</strong> Information provided by Users or Creative Suppliers via email or contact forms (e.g., names, email addresses, and professional portfolio links).</li>
                    <li><strong>Technical Logs:</strong> Categorised as "online identifiers," including IP addresses, browser types, and usage timestamps collected automatically for security and analytical purposes.</li>
                  </ul>

                  <h3>21.2. Lawful Basis for Processing</h3>
                  <p>Data is processed under the following legal bases:</p>
                  <ul>
                    <li><strong>Legitimate Interests:</strong> To maintain website security, monitor traffic patterns, and respond to enquiries from potential clients or creative partners.</li>
                    <li><strong>Consent:</strong> Where a User or Creative Supplier explicitly submits information for the purpose of a specific enquiry or business proposal.</li>
                  </ul>

                  <h3>21.3. Data Retention and Security</h3>
                  <p>The Company implements appropriate technical measures to protect the limited data it holds. Personal data is retained only for the duration necessary to fulfil the specific purpose of the communication or to comply with statutory limitation periods under UK law. Inactive correspondence is purged periodically.</p>

                  <h3>21.4. Third-Party Disclosure</h3>
                  <p>The Company does not sell or lease personal data. Information may be disclosed to third-party service providers (such as web hosting or email delivery services) solely to facilitate the operation of the website. Data may also be disclosed if required by a legal obligation or a court order.</p>

                  <h3>21.5. Statutory Rights</h3>
                  <p>Pursuant to UK data protection law, individuals retain the following rights:</p>
                  <ul>
                    <li>The right to be informed of data processing.</li>
                    <li>The right of access to any data held.</li>
                    <li>The right to rectification of inaccurate data.</li>
                    <li>The right to erasure ("right to be forgotten").</li>
                  </ul>
                  <p>Requests to exercise these rights must be submitted in writing to <a href="mailto:admin@yourplat4orm.com">admin@yourplat4orm.com</a></p>

                  <h3>21.6. Supervisory Authority</h3>
                  <p>The Company is registered with the Information Commissioner's Office, individuals maintain the right to lodge a complaint with the ICO regarding the Company's data handling practices.</p>
                </section>

                {/* Section 22 */}
                <section id="section-22" className="legal-section">
                  <h2 className="heading-font">22. Dispute Resolution</h2>
                  <p><strong>22.1.</strong> If a dispute arises between you and Yourplat4orm in connection with these Terms or your use of the Platform, both parties agree to first attempt to resolve the matter informally by contacting us at <a href="mailto:admin@yourplat4orm.com">admin@yourplat4orm.com</a>.</p>
                  <p><strong>22.2.</strong> We will aim to acknowledge and respond to all dispute notices within a reasonable timeframe and work in good faith to reach a fair resolution.</p>
                  <p><strong>22.3.</strong> Where a dispute cannot be resolved informally, Yourplat4orm will review the matter and make a final decision in its sole discretion. This decision will be binding on users in relation to their use of the Platform, including account status, content decisions, and access to services.</p>
                  <p><strong>22.4.</strong> Nothing in this section affects any rights you may have under applicable law.</p>
                </section>

                {/* Contact Section */}
                <section className="legal-section contact-section">
                  <h2 className="heading-font">Contact Us</h2>
                  <p>If you have any questions about these Terms or the Platform, email us at:</p>
                  <p className="contact-email"><a href="mailto:admin@yourplat4orm.com">admin@yourplat4orm.com</a></p>
                </section>

                </div>
              </div>
            </div>

            {/* Back to Top Button */}
            <button className="back-to-top" onClick={scrollToTop} aria-label="Back to top">
              ↑
            </button>
          </main>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
